import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Globe, ArrowLeft, Send, CheckCircle2, ShieldAlert, FileText, Check, AlertTriangle, HelpCircle, X } from 'lucide-react';
import API_BASE_URL from '../config';
import './CulturalGuestPage.css';

// i18n Dictionary for 5 languages (English, Hindi, French, Spanish, German)
const TRANSLATIONS: Record<string, any> = {
    en: {
        title: "Be a Wedding Guest",
        subtitle: "Experience the magic of traditional Indian weddings first-hand",
        backButton: "Back to Shop",
        formTitle: "Registration Form",
        fullName: "Full Name",
        passportNumber: "Passport / ID Number",
        nationality: "Country of Origin",
        contactNumber: "Mobile Number (with country code)",
        email: "Email Address",
        preferredDate: "Travel / Wedding Date",
        targetCity: "Preferred Cities in India (e.g. Jaipur, Goa)",
        travelersCount: "Travelers in Party",
        nativeLanguages: "Native Language(s)",
        dietary: "Dietary Preferences",
        specialNotes: "Special Notes / Requirements",
        termsTitle: "Terms & Conditions",
        termsIntro: "Please review and accept our cultural guidelines before submitting:",
        term1: "Respect all sacred rituals, holy shrines, and temple premises.",
        term2: "Dress in appropriate semi-formal attire (traditional Indian wear is highly encouraged).",
        term3: "Photography is allowed except during specific holy rituals (as directed by the host planner).",
        term4: "No consumption of alcohol or non-vegetarian food inside sacred temple boundaries.",
        term5: "The host wedding family welcomes you out of pure hospitality. Treat them with gratitude.",
        acceptTerms: "I accept the cultural guidelines and terms and conditions.",
        submitBtn: "Submit Request to Agencies",
        submitting: "Submitting Request...",
        successHeader: "Registration Broadcasted!",
        successBody: "Your travel concierge request has been successfully shared with top wedding agencies. They will coordinate host families and contact you shortly.",
        errorTitle: "Form Error",
        loadingAgencies: "Loading coordinators...",
        readMoreTerms: "Read Full Terms & Conditions",
        detailedTermsTitle: "Cultural Guest Program - Terms & Conditions",
        detailedTerm1: "Eligibility: Participation is strictly restricted to foreign tourists holding valid passports and entry visas for India.",
        detailedTerm2: "Atithi Devo Bhava: Placements are offered as a gesture of traditional Indian hospitality. Guests are expected to treat the host family and their guests with utmost respect and gratitude.",
        detailedTerm3: "No Commercial Solicitation: Guests are prohibited from engaging in any business, marketing, or commercial activity during the wedding events.",
        detailedTerm4: "Safety & Liability: Shadigram and its partner agencies act as coordinators only. We are not liable for any personal travel hazards, medical issues, or disputes arising between hosts and guests.",
        detailedTerm5: "Customs and Etiquette: Strict adherence to temple decorum is required. Guests must comply with instructions regarding removing footwear, restricted areas, and specific photography bans.",
        placeholderName: "e.g. John Doe",
        placeholderPassport: "Passport or ID Card details",
        placeholderNationality: "e.g. United States",
        placeholderContact: "e.g. +1 234 567 8900",
        placeholderEmail: "name@example.com",
        placeholderCity: "e.g. Udaipur, Jaipur, Goa",
        placeholderLanguages: "e.g. English, Spanish",
        placeholderNotes: "Describe your travel vision, physical assistance, or translator requests...",
        dietVeg: "Vegetarian",
        dietNonVeg: "Non-Vegetarian",
        dietVegan: "Vegan",
        dietGlutenFree: "Gluten-Free"
    },
    hi: {
        title: "शादी में मेहमान बनें",
        subtitle: "पारंपरिक भारतीय शादियों के जादू का प्रत्यक्ष अनुभव करें",
        backButton: "दुकान पर वापस जाएं",
        formTitle: "पंजीकरण फॉर्म",
        fullName: "पूरा नाम",
        passportNumber: "पासपोर्ट / आईडी नंबर",
        nationality: "मूल देश",
        contactNumber: "मोबाइल नंबर (देश कोड के साथ)",
        email: "ईमेल पता",
        preferredDate: "यात्रा / विवाह तिथि",
        targetCity: "भारत में पसंदीदा शहर (जैसे जयपुर, गोवा)",
        travelersCount: "यात्रियों की संख्या",
        nativeLanguages: "मातृभाषा",
        dietary: "खान-पान की प्राथमिकताएं",
        specialNotes: "विशेष नोट्स / आवश्यकताएं",
        termsTitle: "नियम एवं शर्तें",
        termsIntro: "कृपया सबमिट करने से पहले हमारे सांस्कृतिक दिशानिर्देशों को स्वीकार करें:",
        term1: "सभी पवित्र अनुष्ठानों, पवित्र स्थलों और मंदिर परिसरों का सम्मान करें।",
        term2: "उपयुक्त अर्ध-औपचारिक पोशाक पहनें (पारंपरिक भारतीय पोशाक को अत्यधिक प्रोत्साहित किया जाता है)।",
        term3: "विशिष्ट पवित्र अनुष्ठानों को छोड़कर फोटोग्राफी की अनुमति है (योजनाकार के निर्देशानुसार)।",
        term4: "पवित्र मंदिर की सीमाओं के भीतर शराब या मांसाहारी भोजन का सेवन न करें।",
        term5: "शादी का परिवार शुद्ध आतिथ्य के तहत आपका स्वागत करता है। उनका सम्मान करें।",
        acceptTerms: "मैं सांस्कृतिक दिशानिर्देशों और नियमों और शर्तों को स्वीकार करता हूँ।",
        submitBtn: "एजेंसियों को अनुरोध सबमिट करें",
        submitting: "अनुरोध सबमिट किया जा रहा है...",
        successHeader: "पंजीकरण प्रसारित किया गया!",
        successBody: "आपका यात्रा द्वारपाल अनुरोध शीर्ष विवाह एजेंसियों के साथ साझा किया गया है। वे मेजबान परिवारों के साथ समन्वय करेंगे और जल्द ही आपसे संपर्क करेंगे।",
        errorTitle: "Form Error",
        loadingAgencies: "समन्वयक लोड हो रहे हैं...",
        readMoreTerms: "नियम एवं शर्तें पूरी पढ़ें",
        detailedTermsTitle: "सांस्कृतिक अतिथि कार्यक्रम - नियम एवं शर्तें",
        detailedTerm1: "पात्रता: भागीदारी केवल वैध पासपोर्ट और भारत के लिए प्रवेश वीजा रखने वाले विदेशी पर्यटकों तक ही सीमित है।",
        detailedTerm2: "अतिथि देवो भव: यह भागीदारी पारंपरिक भारतीय आतिथ्य के सम्मान के रूप में दी जाती है। मेहमानों से अपेक्षा की जाती है कि वे मेजबान परिवार और उनके मेहमानों के साथ अत्यंत सम्मान से पेश आएं।",
        detailedTerm3: "कोई व्यावसायिक आग्रह नहीं: विवाह कार्यक्रमों के दौरान मेहमानों को किसी भी व्यावसायिक, विपणन या व्यावसायिक गतिविधि में शामिल होने की मनाही है।",
        detailedTerm4: "सुरक्षा और दायित्व: शादीग्राम और इसकी सहयोगी एजेंसियां केवल समन्वयक के रूप में कार्य करती हैं। हम किसी भी व्यक्तिगत यात्रा खतरों, चिकित्सा समस्याओं या विवादों के लिए उत्तरदायी नहीं हैं।",
        detailedTerm5: "रीति-रिवाज और शिष्टाचार: मंदिर की मर्यादा का कड़ाई से पालन आवश्यक है। जूते उतारने, प्रतिबंधित क्षेत्रों और विशिष्ट फोटोग्राफी प्रतिबंधों के निर्देशों का पालन करना होगा।",
        placeholderName: "जैसे: जॉन डो",
        placeholderPassport: "पासपोर्ट या आईडी कार्ड का विवरण",
        placeholderNationality: "जैसे: संयुक्त राज्य अमेरिका",
        placeholderContact: "जैसे: +91 98765 43210",
        placeholderEmail: "name@example.com",
        placeholderCity: "जैसे: उदयपुर, जयपुर, गोवा",
        placeholderLanguages: "जैसे: अंग्रेजी, स्पेनिश",
        placeholderNotes: "अपनी यात्रा का विवरण, शारीरिक सहायता, या अनुवादक अनुरोध लिखें...",
        dietVeg: "शाकाहारी",
        dietNonVeg: "मांसाहारी",
        dietVegan: "वेगान",
        dietGlutenFree: "ग्लूटेन-मुक्त"
    },
    fr: {
        title: "Devenir Invité de Mariage",
        subtitle: "Découvrez la magie des mariages indiens traditionnels en direct",
        backButton: "Retour à la boutique",
        formTitle: "Formulaire d'inscription",
        fullName: "Nom complet",
        passportNumber: "Numéro de passeport / ID",
        nationality: "Pays d'origine",
        contactNumber: "Numéro de mobile (avec code pays)",
        email: "Adresse e-mail",
        preferredDate: "Date de voyage / mariage",
        targetCity: "Villes préférées en Inde (ex. Jaipur, Goa)",
        travelersCount: "Nombre de voyageurs",
        nativeLanguages: "Langue(s) maternelle(s)",
        dietary: "Préférences alimentaires",
        specialNotes: "Notes spéciales / Exigences",
        termsTitle: "Conditions Générales",
        termsIntro: "Veuillez lire et accepter nos directives culturelles avant de soumettre:",
        term1: "Respectez tous les rituels sacrés, les lieux saints et les temples.",
        term2: "Portez une tenue semi-formelle appropriée (la tenue indienne traditionnelle est fortement encouragée).",
        term3: "La photographie est autorisée sauf pendant les rituels sacrés spécifiques (comme indiqué par l'organisateur).",
        term4: "Pas de consommation d'alcool ni de nourriture non végétarienne à l'intérieur des temples sacrés.",
        term5: "La famille d'accueil vous accueille par pure hospitalité. Traitez-les avec gratitude.",
        acceptTerms: "J'accepte les directives culturelles et les conditions générales.",
        submitBtn: "Soumettre la demande aux agences",
        submitting: "Soumission de la demande...",
        successHeader: "Inscription diffusée!",
        successBody: "Votre demande de conciergerie de voyage a été partagée avec les meilleures agences de mariage. Elles coordonneront avec les familles d'accueil et vous contacteront bientôt.",
        errorTitle: "Erreur de formulaire",
        loadingAgencies: "Chargement des coordinateurs...",
        readMoreTerms: "Lire les conditions générales complètes",
        detailedTermsTitle: "Programme d'invités culturels - Conditions générales",
        detailedTerm1: "Éligibilité : La participation est strictement réservée aux touristes étrangers titulaires d'un passeport valide et d'un visa d'entrée pour l'Inde.",
        detailedTerm2: "Atithi Devo Bhava : Les placements sont offerts comme un geste d'hospitalité indienne traditionnelle. Les invités doivent traiter la famille d'accueil avec le plus grand respect.",
        detailedTerm3: "Pas de sollicitation commerciale : Il est interdit aux invités de s'engager dans des activités commerciales, marketing ou professionnelles pendant les événements.",
        detailedTerm4: "Sécurité & Responsabilité : Shadigram et ses agences partenaires agissent uniquement en tant que coordinateurs. Nous ne sommes pas responsables des risques de voyage ou des litiges.",
        detailedTerm5: "Coutumes et étiquette : Le respect strict du décorum du temple est requis. Les invités doivent se conformer aux instructions concernant le retrait des chaussures et les interdictions de photographier.",
        placeholderName: "ex. Jean Dupont",
        placeholderPassport: "Détails du passeport ou de la carte d'identité",
        placeholderNationality: "ex. France, Canada",
        placeholderContact: "ex. +33 6 1234 5678",
        placeholderEmail: "nom@exemple.com",
        placeholderCity: "ex. Udaipur, Jaipur, Goa",
        placeholderLanguages: "ex. Français, Anglais",
        placeholderNotes: "Décrivez votre vision de voyage, assistance physique ou demandes de traducteur...",
        dietVeg: "Végétarien",
        dietNonVeg: "Non-végétarien",
        dietVegan: "Végétalien",
        dietGlutenFree: "Sans gluten"
    },
    es: {
        title: "Ser un Invitado de Boda",
        subtitle: "Experimente la magia de las bodas tradicionales de la India en persona",
        backButton: "Volver a la tienda",
        formTitle: "Formulario de registro",
        fullName: "Nombre completo",
        passportNumber: "Número de pasaporte / ID",
        nationality: "País de origen",
        contactNumber: "Número de móvil (con código de país)",
        email: "Correo electrónico",
        preferredDate: "Fecha de viaje / boda",
        targetCity: "Ciudades preferidas en la India (ej. Jaipur, Goa)",
        travelersCount: "Número de viajeros",
        nativeLanguages: "Idioma(s) nativo(s)",
        dietary: "Preferencias dietéticas",
        specialNotes: "Notas especiales / Requisitos",
        termsTitle: "Términos y Condiciones",
        termsIntro: "Por favor revise y acepte nuestras pautas culturales antes de enviar:",
        term1: "Respete todos los rituales sagrados, santuarios y templos.",
        term2: "Use vestimenta semiformal apropiada (se fomenta la vestimenta tradicional de la India).",
        term3: "Se permite la fotografía excepto durante rituales sagrados específicos (según lo indique el organizador).",
        term4: "No se permite consumir alcohol ni comida no vegetariana dentro de los límites sagrados del templo.",
        term5: "La familia anfitriona le da la bienvenida por pura hospitalidad. Trátelos con gratitud.",
        acceptTerms: "Acepto las pautas culturales y los términos y condiciones.",
        submitBtn: "Enviar solicitud a las agencias",
        submitting: "Enviando solicitud...",
        successHeader: "¡Registro transmitido!",
        successBody: "Su solicitud de conserjería de viaje se ha compartido con las mejores agencias de bodas. Coordinarán con las familias anfitrionas y se comunicarán con usted pronto.",
        errorTitle: "Error de formulario",
        loadingAgencies: "Cargando coordinadores...",
        readMoreTerms: "Leer los términos y condiciones completos",
        detailedTermsTitle: "Programa de invitados culturales - Términos y condiciones",
        detailedTerm1: "Elegibilidad: La participación está estrictamente restringida a turistas extranjeros con pasaportes válidos y visas de entrada para la India.",
        detailedTerm2: "Atithi Devo Bhava: Los alojamientos se ofrecen como un gesto de hospitalidad tradicional de la India. Se espera que los invitados traten a la familia anfitriona con respeto.",
        detailedTerm3: "Sin solicitud comercial: Los invitados tienen prohibido participar en cualquier actividad comercial, de marketing o comercial durante los eventos de la boda.",
        detailedTerm4: "Seguridad y responsabilidad: Shadigram y sus agencias asociadas actúan únicamente como coordinadores. No somos responsables de los riesgos de viaje o disputas.",
        detailedTerm5: "Costumbres y etiqueta: Se requiere un estricto cumplimiento del decoro del templo. Los invitados deben cumplir con las instrucciones sobre quitarse el calzado y prohibiciones de fotografía.",
        placeholderName: "ej. Juan Pérez",
        placeholderPassport: "Detalles del pasaporte o documento de identidad",
        placeholderNationality: "ej. España, México",
        placeholderContact: "ej. +34 612 345 678",
        placeholderEmail: "nombre@ejemplo.com",
        placeholderCity: "ej. Udaipur, Jaipur, Goa",
        placeholderLanguages: "ej. Español, Inglés",
        placeholderNotes: "Describa sus detalles de viaje, asistencia física o solicitudes de traducción...",
        dietVeg: "Vegetariano",
        dietNonVeg: "No vegetariano",
        dietVegan: "Vegano",
        dietGlutenFree: "Sin gluten"
    },
    de: {
        title: "Ein Hochzeitsgast sein",
        subtitle: "Erleben Sie den Zauber traditioneller indischer Hochzeiten hautnah",
        backButton: "Zurück zum Shop",
        formTitle: "Anmeldeformular",
        fullName: "Vollständiger Name",
        passportNumber: "Reisepass- / Ausweisnummer",
        nationality: "Herkunftsland",
        contactNumber: "Mobilnummer (mit Ländervorwahl)",
        email: "E-Mail-Adresse",
        preferredDate: "Reise- / Hochzeitsdatum",
        targetCity: "Bevorzugte Städte in Indien (z. B. Jaipur, Goa)",
        travelersCount: "Anzahl der Reisenden",
        nativeLanguages: "Muttersprache(n)",
        dietary: "Ernährungspräferenzen",
        specialNotes: "Besondere Hinweise / Anforderungen",
        termsTitle: "Allgemeine Geschäftsbedingungen",
        termsIntro: "Bitte lesen und akzeptieren Sie unsere kulturellen Richtlinien, bevor Sie fortfahren:",
        term1: "Respektieren Sie alle heiligen Rituale, Schreine und Tempelbereiche.",
        term2: "Tragen Sie angemessene halboformelle Kleidung (traditionelle indische Kleidung wird empfohlen).",
        term3: "Fotografieren ist erlaubt, außer während spezifischer heiliger Rituale (nach Anweisung des Planers).",
        term4: "Kein Konsum von Alkohol oder nicht-vegetarischen Lebensmitteln innerhalb der Tempelgrenzen.",
        term5: "Die Gastfamilie empfängt Sie aus reiner Gastfreundschaft. Behandeln Sie sie mit Dankbarkeit.",
        acceptTerms: "Ich akzeptiere die kulturellen Richtlinien und die allgemeinen Geschäftsbedingungen.",
        submitBtn: "Anfrage an Agenturen senden",
        submitting: "Anfrage wird gesendet...",
        successHeader: "Registrierung übertragen!",
        successBody: "Ihre Anfrage wurde erfolgreich an die besten Hochzeitsagenturen übermittelt. Diese werden sich mit den Gastfamilien abstimmen und sich in Kürze mit Ihnen in Verbindung setzen.",
        errorTitle: "Formularfehler",
        loadingAgencies: "Koordinatoren werden geladen...",
        readMoreTerms: "Vollständige AGB lesen",
        detailedTermsTitle: "Kulturelles Gästeprogramm - Allgemeine Geschäftsbedingungen",
        detailedTerm1: "Teilnahmeberechtigung: Die Teilnahme ist streng auf ausländische Touristen mit gültigen Reisepässen und Einreisevisa für Indien beschränkt.",
        detailedTerm2: "Atithi Devo Bhava: Die Vermittlung erfolgt als Geste traditioneller indischer Gastfreundschaft. Gäste sollten der Gastfamilie mit größtem Respekt begegnen.",
        detailedTerm3: "Keine kommerzielle Werbung: Gästen ist es untersagt, während der Hochzeitsveranstaltungen geschäftliche, Marketing- oder kommerzielle Aktivitäten durchzuführen.",
        detailedTerm4: "Sicherheit & Haftung: Shadigram und seine Partneragenturen agieren nur als Koordinatoren. Wir haften nicht für Reiserisiken, medizinische Probleme oder Streitigkeiten.",
        detailedTerm5: "Bräuche und Knigge: Die Einhaltung des Tempel-Dekorums ist zwingend erforderlich. Gäste müssen die Anweisungen zum Ausziehen von Schuhen und Fotografierverboten befolgen.",
        placeholderName: "z. B. Max Mustermann",
        placeholderPassport: "Reisepass- oder Personalausweisdetails",
        placeholderNationality: "z. B. Deutschland, Österreich",
        placeholderContact: "z. B. +49 170 1234567",
        placeholderEmail: "name@beispiel.de",
        placeholderCity: "z. B. Udaipur, Jaipur, Goa",
        placeholderLanguages: "z. B. Deutsch, Englisch",
        placeholderNotes: "Beschreiben Sie Ihre Reisevorstellungen, körperliche Unterstützung oder Übersetzerwünsche...",
        dietVeg: "Vegetarisch",
        dietNonVeg: "Nicht vegetarisch",
        dietVegan: "Vegan",
        dietGlutenFree: "Glutenfrei"
    }
};

const CulturalGuestPage: React.FC = () => {
    const history = useHistory();
    const [lang, setLang] = useState<'en' | 'hi' | 'fr' | 'es' | 'de'>('en');
    
    // Form States
    const [fullName, setFullName] = useState('');
    const [passport, setPassport] = useState('');
    const [nationality, setNationality] = useState('');
    const [contact, setContact] = useState('');
    const [email, setEmail] = useState('');
    const [weddingDate, setWeddingDate] = useState('');
    const [city, setCity] = useState('');
    const [travelers, setTravelers] = useState(1);
    const [languages, setLanguages] = useState('');
    const [dietary, setDietary] = useState('veg');
    const [notes, setNotes] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);

    const [loading, setLoading] = useState(false);
    const [agencies, setAgencies] = useState<any[]>([]);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Dynamic Terms from DB
    const [dbTerms, setDbTerms] = useState<Record<string, string[]>>({
        en: [], hi: [], fr: [], es: [], de: []
    });

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/public/cultural-guest/terms`)
            .then(res => res.json())
            .then(data => {
                if (data.success && data.terms) {
                    setDbTerms(data.terms);
                }
            })
            .catch(err => console.error("Error fetching terms from DB:", err));
    }, []);

    const t = TRANSLATIONS[lang];

    // Fetch active agencies to broadcast the guest enquiry
    useEffect(() => {
        fetch(`${API_BASE_URL}/api/public/agencies`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setAgencies(data.agencies || []);
                }
            })
            .catch(err => console.error("Error fetching coordinator agencies:", err));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!accepted) {
            setError(lang === 'hi' ? 'कृपया नियमों और शर्तों को स्वीकार करें।' : 'Please accept the terms and conditions.');
            return;
        }

        if (agencies.length === 0) {
            setError(lang === 'hi' ? 'कोई समन्वयक एजेंसी उपलब्ध नहीं है।' : 'No coordinator agencies are currently available.');
            return;
        }

        setLoading(true);

        try {
            // Compose the detailed guest notes for the agency dashboard CRM
            const detailedNotes = `[CULTURAL TOURIST GUEST PROGRAM]
Passport Number: ${passport}
Nationality: ${nationality}
Native Languages: ${languages}
Dietary Preferences: ${dietary.toUpperCase()}
Custom Vision / Travel Details:
${notes}`;

            // Map travelers count to expectedGuests and city to weddingCity
            const response = await fetch(`${API_BASE_URL}/api/public/agency/enquiry/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agencyIds: agencies.map(a => a._id),
                    clientName: `${fullName} (International Guest)`,
                    primaryMobile: contact,
                    email,
                    weddingDate,
                    city,
                    expectedGuests: travelers,
                    notes: detailedNotes
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to submit registration');
            }

            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                history.push('/marketplace');
            }, 4000);
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please check your network connection.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cultural-guest-app">
            {/* Glowing background shapes for premium aesthetics */}
            <div className="bg-glow-blob bg-glow-left"></div>
            <div className="bg-glow-blob bg-glow-right"></div>
            {/* Header / Nav */}
            <nav className="guest-nav glass">
                <button className="back-link" onClick={() => history.push('/marketplace')}>
                    <ArrowLeft size={16} />
                    <span>{t.backButton}</span>
                </button>

                <div className="premium-lang-dropdown-container">
                    <Globe size={16} className="lang-dropdown-icon" />
                    <select
                        value={lang}
                        onChange={(e) => setLang(e.target.value as any)}
                        className="premium-lang-select"
                    >
                        <option value="en">English (🇬🇧)</option>
                        <option value="hi">हिन्दी (🇮🇳)</option>
                        <option value="fr">Français (🇫🇷)</option>
                        <option value="es">Español (🇪🇸)</option>
                        <option value="de">Deutsch (🇩🇪)</option>
                    </select>
                </div>
            </nav>

            <main className="guest-container">
                <section className="guest-hero-section">
                    <span className="hero-tagline">{t.subtitle}</span>
                    <h1 className="hero-heading">{t.title}</h1>
                </section>

                {success ? (
                    <div className="guest-success-card glass animate-scaleUp">
                        <div className="success-glow">
                            <CheckCircle2 size={70} className="success-icon" />
                        </div>
                        <h2>{t.successHeader}</h2>
                        <p>{t.successBody}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="guest-workspace-grid">
                        {/* Left Side: Enquiry Form */}
                        <div className="guest-form-card glass">
                            <h3>{t.formTitle}</h3>
                            {error && (
                                <div className="form-error-banner">
                                    <ShieldAlert size={16} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <div className="guest-form">
                                <div className="form-row">
                                    <div className="form-input-group">
                                        <label>{t.fullName} *</label>
                                        <input 
                                            type="text" 
                                            required 
                                            value={fullName}
                                            onChange={e => setFullName(e.target.value)}
                                            placeholder={t.placeholderName}
                                        />
                                    </div>
                                    <div className="form-input-group">
                                        <label>{t.passportNumber} *</label>
                                        <input 
                                            type="text" 
                                            required 
                                            value={passport}
                                            onChange={e => setPassport(e.target.value)}
                                            placeholder={t.placeholderPassport}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-input-group">
                                        <label>{t.nationality} *</label>
                                        <input 
                                            type="text" 
                                            required 
                                            value={nationality}
                                            onChange={e => setNationality(e.target.value)}
                                            placeholder={t.placeholderNationality}
                                        />
                                    </div>
                                    <div className="form-input-group">
                                        <label>{t.contactNumber} *</label>
                                        <input 
                                            type="tel" 
                                            required 
                                            value={contact}
                                            onChange={e => setContact(e.target.value)}
                                            placeholder={t.placeholderContact}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-input-group">
                                        <label>{t.email} *</label>
                                        <input 
                                            type="email" 
                                            required 
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            placeholder={t.placeholderEmail}
                                        />
                                    </div>
                                    <div className="form-input-group">
                                        <label>{t.preferredDate} *</label>
                                        <input 
                                            type="date" 
                                            required 
                                            value={weddingDate}
                                            onChange={e => setWeddingDate(e.target.value)}
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-input-group">
                                        <label>{t.targetCity} *</label>
                                        <input 
                                            type="text" 
                                            required 
                                            value={city}
                                            onChange={e => setCity(e.target.value)}
                                            placeholder={t.placeholderCity}
                                        />
                                    </div>
                                    <div className="form-input-group">
                                        <label>{t.travelersCount} *</label>
                                        <input 
                                            type="number" 
                                            required 
                                            min={1} 
                                            max={100}
                                            value={travelers}
                                            onChange={e => setTravelers(parseInt(e.target.value))}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-input-group">
                                        <label>{t.nativeLanguages} *</label>
                                        <input 
                                            type="text" 
                                            required 
                                            value={languages}
                                            onChange={e => setLanguages(e.target.value)}
                                            placeholder={t.placeholderLanguages}
                                        />
                                    </div>
                                    <div className="form-input-group">
                                        <label>{t.dietary} *</label>
                                        <select value={dietary} onChange={e => setDietary(e.target.value)}>
                                            <option value="veg">{t.dietVeg}</option>
                                            <option value="non-veg">{t.dietNonVeg}</option>
                                            <option value="vegan">{t.dietVegan}</option>
                                            <option value="gluten-free">{t.dietGlutenFree}</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-input-group">
                                    <label>{t.specialNotes}</label>
                                    <textarea 
                                        rows={3}
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                        placeholder={t.placeholderNotes}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Interactive Terms & Acceptances */}
                        <div className="guest-terms-card glass">
                            <div className="terms-header">
                                <FileText size={20} className="terms-icon" />
                                <h3>{t.termsTitle}</h3>
                            </div>
                            <p className="terms-intro">{t.termsIntro}</p>

                            <div className="terms-list">
                                {((dbTerms[lang] && dbTerms[lang].length > 0) 
                                    ? dbTerms[lang] 
                                    : [t.term1, t.term2, t.term3, t.term4, t.term5]
                                 ).map((term, index) => (
                                     <div className="term-item" key={index}>
                                         <div className="term-bullet"><Check size={12} /></div>
                                         <p>{term}</p>
                                     </div>
                                 ))}
                            </div>

                            <div className="terms-acceptance-row" onClick={() => setAccepted(!accepted)}>
                                <div className={`terms-checkbox ${accepted ? 'checked' : ''}`}>
                                    {accepted && <Check size={12} color="#fff" />}
                                </div>
                                <label>{t.acceptTerms}</label>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-1rem', marginBottom: '1.5rem' }}>
                                <button 
                                    type="button" 
                                    className="read-terms-link" 
                                    onClick={(e) => { e.stopPropagation(); setShowTermsModal(true); }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#d4af37',
                                        textDecoration: 'underline',
                                        fontSize: '0.85rem',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        padding: 0,
                                        transition: 'color 0.2s'
                                    }}
                                >
                                    {t.readMoreTerms}
                                </button>
                            </div>

                            <div className="terms-info-box">
                                <AlertTriangle size={18} className="info-icon" />
                                <p><strong>Travel Warning:</strong> Please ensure your Indian Tourist Visa is valid before departure. Local agencies handle guest placements inside verified family weddings only.</p>
                            </div>

                            <button 
                                type="submit" 
                                disabled={loading || !accepted} 
                                className="guest-submit-btn"
                                style={{ width: '100%', marginTop: '1.5rem' }}
                            >
                                {loading ? t.submitting : <>{t.submitBtn} <Send size={14} /></>}
                            </button>
                        </div>
                    </form>
                )}
            </main>

            {/* Detailed Terms Modal */}
            {showTermsModal && (
                <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
                    <div className="terms-modal-content glass animate-scaleUp" onClick={(e) => e.stopPropagation()}>
                        <div className="terms-modal-header">
                            <h3>{t.detailedTermsTitle}</h3>
                            <button className="close-modal-btn" onClick={() => setShowTermsModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="terms-modal-body">
                            {((dbTerms[lang] && dbTerms[lang].length > 0)
                                ? dbTerms[lang]
                                : [
                                    `Eligibility: ${t.detailedTerm1.split(': ')[1] || t.detailedTerm1}`, 
                                    `Atithi Devo Bhava: ${t.detailedTerm2.split(': ')[1] || t.detailedTerm2}`, 
                                    `No Commercial Solicitation: ${t.detailedTerm3.split(': ')[1] || t.detailedTerm3}`, 
                                    `Safety & Liability: ${t.detailedTerm4.split(': ')[1] || t.detailedTerm4}`, 
                                    `Customs and Etiquette: ${t.detailedTerm5.split(': ')[1] || t.detailedTerm5}`
                                  ]
                            ).map((term, index) => {
                                const parts = term.split(': ');
                                const title = parts[0];
                                const desc = parts[1] || '';
                                return (
                                    <div className="modal-term-item" key={index}>
                                        <h5>{index + 1}. {title}</h5>
                                        {desc && <p>{desc}</p>}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="terms-modal-footer">
                            <button className="btn-close-modal" onClick={() => setShowTermsModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CulturalGuestPage;
